import React, { useState, useEffect, useRef, useCallback } from 'react';

function NoteModal({
  isOpen,
  onClose,
  note = "",
  onSave,
  history = [],   // Array of previous notes (optional)
  maxLength = 500
}) {
  const [draft, setDraft] = useState(note);
  const [star, setStar] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const textareaRef = useRef();

  // Reset state on open/note change
  useEffect(() => {
    if (isOpen) {
      setDraft(note);
      setShowSaved(false);
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [note, isOpen]);

  // Save handler (hoisted for effect usage)
  const handleSave = useCallback(() => {
    if (draft.trim()) {
      onSave(draft, star);
      setShowSaved(true);
      setTimeout(() => {
        setShowSaved(false);
        onClose();
      }, 800); // Let user see "Saved!" flash
    }
  }, [draft, star, onSave, onClose]);

  // Keyboard shortcuts: ESC to close, Cmd/Ctrl+Enter to save
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, handleSave, onClose]);

  // Copy-to-clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 700);
    } catch {
      // Fallback for older browsers
      window.prompt("Copy to clipboard: Ctrl+C, Enter", draft);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="modal-backdrop">
      <div className="modal note-modal" role="dialog" aria-modal="true" aria-label="Take a Note">
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ margin: 0, flex: 1 }}>📝 Note</h3>
          <button
            aria-label={star ? "Mark as not important" : "Mark as important"}
            onClick={() => setStar(v => !v)}
            title={star ? "Marked Important" : "Mark as Important"}
            style={{ fontSize: 22, background: "none", border: "none", cursor: "pointer", color: star ? "#f6ad55" : "#bbb" }}
          >★</button>
        </div>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Type your note here…"
          rows={5}
          maxLength={maxLength}
          style={{ width: "100%", marginBottom: 8, fontSize: 16, borderRadius: 7, padding: 7, border: "1.5px solid #dde" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ color: draft.length > maxLength * 0.92 ? "#e55b5b" : "#888", fontSize: 13 }}>
            {draft.length}/{maxLength}
          </span>
          <div style={{ display: "flex", gap: 7 }}>
            <button type="button" onClick={handleCopy} style={{ background: "#eee", border: "none", borderRadius: 5, cursor: "pointer", padding: "3px 10px" }}>Copy</button>
            <button type="button" onClick={onClose} style={{ background: "#ccc", color: "#333", borderRadius: 5 }}>Cancel</button>
            <button type="button" onClick={handleSave} disabled={!draft.trim()} style={{ background: "#2474f4", color: "#fff", borderRadius: 5 }}>Save</button>
          </div>
        </div>
        {showSaved && <div style={{ color: "#38b000", fontWeight: 600, fontSize: 15 }}>Saved!</div>}
        {history?.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 2 }}>Previous notes:</div>
            <ul style={{ fontSize: 14, paddingLeft: 14, marginBottom: 0 }}>
              {history.map((n, i) => (
                <li key={i} style={{ color: "#555" }}>{n}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default NoteModal;
